import { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import axios from 'axios';
import authApi from '@services/authApi.jsx';
import '@assets/css/auth/loginPendingApproval.css';

const API_BASE_URL = import.meta.env.VITE_ADMIN_BACKEND_API_URL || '/api/v1';

export default function LoginPendingApproval() {
    const navigate = useNavigate();
    const location = useLocation();

    // SignupToken 상태 관리
    const [signupToken, setSignupToken] = useState(null);
    const [tokenData, setTokenData] = useState(null);  // 토큰에서 추출한 사용자 정보

    // 데이터 로딩 상태 관리
    const [branchList, setBranchList] = useState([]);
    const [roleList, setRoleList] = useState([]);
    const [dataLoading, setDataLoading] = useState(true);

    // 폼 상태 관리
    const [formData, setFormData] = useState({
        name: '',
        phone: '',
        userRoles: [{ roleId: null, branchId: null }]
    });

    // 로딩 및 에러 상태 관리
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [error, setError] = useState(null);
    const [success, setSuccess] = useState(false);

    // SignupToken 추출 및 파싱
    useEffect(() => {
        const extractSignupToken = () => {
            try {
                // 1. URL 파라미터에서 추출 (localdev)
                const searchParams = new URLSearchParams(location.search);
                let token = searchParams.get('signup_token');

                // 2. 쿠키에서 추출 (dev/prod)
                if (!token) {
                    const cookies = document.cookie.split(';');
                    for (const cookie of cookies) {
                        const [name, value] = cookie.trim().split('=');
                        if (name === 'signup_token') {
                            token = value;
                            break;
                        }
                    }
                }

                if (!token) {
                    setError('회원가입 토큰이 없습니다. 다시 로그인해주세요.');
                    setDataLoading(false);
                    return;
                }

                setSignupToken(token);

                // 토큰에서 사용자 정보 추출 (JWT 디코딩)
                const tokenParts = token.split('.');
                if (tokenParts.length === 3) {
                    const base64 = tokenParts[1].replace(/-/g, '+').replace(/_/g, '/');
                    const jsonPayload = decodeURIComponent(
                        atob(base64)
                            .split('')
                            .map(c => '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2))
                            .join('')
                    );
                    const payload = JSON.parse(jsonPayload);

                    // 토큰 만료 확인
                    if (payload.exp && payload.exp * 1000 < Date.now()) {
                        setError('회원가입 토큰이 만료되었습니다. 다시 로그인해주세요.');
                        setDataLoading(false);
                        return;
                    }

                    setTokenData({
                        email: payload.sub,
                        name: payload.name || '',
                        provider: payload.provider,
                        providerId: payload.providerId
                    });

                    // 폼 데이터에 이름 설정
                    setFormData(prev => ({
                        ...prev,
                        name: payload.name || ''
                    }));
                }

                // URL에서 토큰 파라미터 제거 (보안)
                if (searchParams.has('signup_token')) {
                    window.history.replaceState({}, '', '/loginPendingApproval');
                }

                setDataLoading(false);
            } catch (err) {
                console.error('SignupToken 처리 실패:', err);
                setError('토큰 처리 중 오류가 발생했습니다.');
                setDataLoading(false);
            }
        };

        extractSignupToken();
    }, [location.search]);

    // 역할 목록 가져오기 (공개 API 사용)
    useEffect(() => {
        async function getRoleList() {
            try {
                const response = await axios.get(`${API_BASE_URL}/auth/signup/roles`);
                const apiRoleData = response?.data?.data || response?.data || [];

                if (Array.isArray(apiRoleData) && apiRoleData.length > 0) {
                    // 역할에 따른 지점 필수 여부 설정
                    // ROLE_ADMIN: 지점 불필요, ROLE_MANAGER/ROLE_USER: 지점 필수
                    const rolesWithBranchRequirement = apiRoleData.map(role => ({
                        ...role,
                        requiresBranch: role.roleType !== 'ROLE_ADMIN'
                    }));

                    const sortedRoles = rolesWithBranchRequirement.sort((a, b) => {
                        const roleTypeOrder = {
                            "ROLE_ADMIN": 1,
                            "ROLE_MANAGER": 2,
                            "ROLE_USER": 3
                        };
                        return (roleTypeOrder[a.roleType] || 99) - (roleTypeOrder[b.roleType] || 99);
                    });

                    setRoleList(sortedRoles);
                } else {
                    // 기본 역할 목록 (API 실패시 폴백)
                    setRoleList([
                        { id: 1, name: '일반 사용자', roleType: 'ROLE_USER', requiresBranch: true },
                        { id: 2, name: '관리자', roleType: 'ROLE_MANAGER', requiresBranch: true },
                        { id: 3, name: '최고 관리자', roleType: 'ROLE_ADMIN', requiresBranch: false }
                    ]);
                }
            } catch (error) {
                console.error('역할 목록 가져오기 실패:', error);
                setRoleList([
                    { id: 1, name: '일반 사용자', roleType: 'ROLE_USER', requiresBranch: true },
                    { id: 2, name: '관리자', roleType: 'ROLE_MANAGER', requiresBranch: true },
                    { id: 3, name: '최고 관리자', roleType: 'ROLE_ADMIN', requiresBranch: false }
                ]);
            }
        }
        getRoleList();
    }, []);

    // 브랜치 목록 가져오기 (공개 API 사용)
    useEffect(() => {
        async function getBranchList() {
            try {
                const response = await axios.get(`${API_BASE_URL}/auth/signup/branches`);
                const branchData = response?.data?.data || response?.data || [];

                if (Array.isArray(branchData)) {
                    const uniqueBranches = branchData.reduce((acc, branch) => {
                        if (!acc.some((item) => item.id === branch.id)) {
                            acc.push(branch);
                        }
                        return acc;
                    }, []);
                    setBranchList(uniqueBranches);
                } else {
                    setBranchList([]);
                }
            } catch (error) {
                console.error('브랜치 목록 가져오기 실패:', error);
                setBranchList([]);
            }
        }
        getBranchList();
    }, []);

    // 입력 필드 변경 처리
    const handleChange = (e) => {
        const { name, value } = e.target;
        setFormData(prevData => ({
            ...prevData,
            [name]: value
        }));
    };

    // 역할이 브랜치를 필요로 하는지 확인
    const checkRoleRequiresBranch = (roleId) => {
        if (!roleId) return true;
        const selectedRole = roleList.find(role => role.id === Number(roleId));

        if (selectedRole) {
            if (selectedRole.requiresBranch !== undefined) {
                return selectedRole.requiresBranch;
            }
            return selectedRole.roleType !== 'ROLE_ADMIN';
        }

        return true;
    };

    // 역할-지점 추가
    const addUserRole = () => {
        const defaultRoleId = roleList.length > 0 ? roleList[0].id : 1;

        setFormData(prevData => ({
            ...prevData,
            userRoles: [
                ...prevData.userRoles,
                { roleId: defaultRoleId, branchId: null }
            ]
        }));
    };

    // 역할-지점 제거
    const removeUserRole = (index) => {
        setFormData(prevData => {
            const newUserRoles = [...prevData.userRoles];
            newUserRoles.splice(index, 1);
            return {
                ...prevData,
                userRoles: newUserRoles
            };
        });
    };

    // 역할-지점 수정
    const updateUserRole = (index, field, value) => {
        setFormData(prevData => {
            const newUserRoles = [...prevData.userRoles];

            if (field === 'roleId' && !checkRoleRequiresBranch(value)) {
                newUserRoles[index] = {
                    ...newUserRoles[index],
                    [field]: value,
                    branchId: null
                };
            } else {
                newUserRoles[index] = {
                    ...newUserRoles[index],
                    [field]: value
                };
            }

            return {
                ...prevData,
                userRoles: newUserRoles
            };
        });
    };

    // 폼 제출 처리
    const handleSubmit = async (e) => {
        e.preventDefault();

        if (!signupToken) {
            setError('회원가입 토큰이 없습니다. 다시 로그인해주세요.');
            return;
        }

        try {
            setIsSubmitting(true);
            setError(null);

            // 회원가입 API 호출 (SignupToken + 폼 데이터)
            const signupData = {
                signupToken: signupToken,
                name: formData.name,
                phone: formData.phone,
                userRoles: formData.userRoles
            };

            const response = await authApi.signup(signupData);

            if (response.success) {
                // signup_token 쿠키 삭제
                document.cookie = 'signup_token=; Max-Age=0; Path=/;';

                setSuccess(true);

                // 성공 메시지 표시 후 승인 대기 페이지로 이동
                setTimeout(() => {
                    navigate('/loginWaitingApproval');
                }, 2000);
            } else {
                setError(response.message || '회원가입 중 오류가 발생했습니다.');
            }

        } catch (error) {
            console.error('회원가입 실패:', error);
            setError(error.response?.data?.message || '회원가입 중 오류가 발생했습니다. 다시 시도해 주세요.');
        } finally {
            setIsSubmitting(false);
        }
    };

    // 로그인 페이지로 이동
    const handleBackToLogin = () => {
        // signup_token 쿠키 삭제
        document.cookie = 'signup_token=; Max-Age=0; Path=/;';
        navigate('/login');
    };

    // 데이터 로딩 중 표시
    if (dataLoading) {
        return <div className="loading">정보를 불러오는 중입니다...</div>;
    }

    // 토큰이 없거나 만료된 경우
    if (!signupToken && !dataLoading) {
        return (
            <div className="login-pending-approval">
                <h2>회원가입</h2>
                <div className="error-message">
                    {error || '회원가입 토큰이 없습니다.'}
                </div>
                <button onClick={handleBackToLogin} className="submit-button">
                    로그인 페이지로 이동
                </button>
            </div>
        );
    }

    return (
        <div className="login-pending-approval">
            <h2>회원가입 - 추가 정보 입력</h2>
            <p>계정 생성을 위해 추가 정보를 입력해 주세요.</p>

            {error && (
                <div className="error-message">
                    {error}
                </div>
            )}

            {success && (
                <div className="success-message">
                    회원가입이 완료되었습니다. 관리자 승인을 기다려 주세요.
                </div>
            )}

            <form onSubmit={handleSubmit}>
                <div className="form-group">
                    <label>
                        <h2>이메일</h2>
                        <input
                            type="email"
                            value={tokenData?.email || ''}
                            readOnly={true}
                            className="readonly-input"
                        />
                        <small className="helper-text">Google 계정 이메일 (변경 불가)</small>
                    </label>
                </div>

                <div className="form-group">
                    <label>
                        <h2>이름</h2>
                        <input
                            type="text"
                            name="name"
                            value={formData.name}
                            onChange={handleChange}
                            placeholder="이름을 입력하세요"
                            required
                        />
                    </label>
                </div>

                <div className="form-group">
                    <label>
                        <h2>연락처</h2>
                        <input
                            type="tel"
                            name="phone"
                            value={formData.phone}
                            onChange={handleChange}
                            placeholder="연락처를 입력하세요"
                            required
                        />
                    </label>
                </div>

                <div className="form-section">
                    <h3>권한 설정</h3>
                    <p>사용자의 역할과 접근 가능한 지점을 설정하세요.</p>

                    {formData.userRoles.map((userRole, index) => (
                        <div key={index} className="user-role-item">
                            <div className="role-header">
                                <h4>권한 #{index + 1}</h4>
                                {formData.userRoles.length > 1 && (
                                    <button
                                        type="button"
                                        className="remove-button"
                                        onClick={() => removeUserRole(index)}
                                    >
                                        삭제
                                    </button>
                                )}
                            </div>

                            <div className="role-fields">
                                <div className="form-group">
                                    <label>
                                        <h2>역할</h2>
                                        <select
                                            value={userRole.roleId || ''}
                                            onChange={(e) => updateUserRole(index, 'roleId', Number(e.target.value))}
                                            required
                                        >
                                            <option value="">역할을 선택하세요</option>
                                            {roleList.map(role => (
                                                <option key={role.id} value={role.id}>
                                                    {role.name}
                                                </option>
                                            ))}
                                        </select>
                                    </label>
                                </div>

                                {userRole.roleId && checkRoleRequiresBranch(userRole.roleId) && (
                                    <div className="form-group">
                                        <label>
                                            <h2>지점</h2>
                                            <select
                                                value={userRole.branchId || ''}
                                                onChange={(e) => updateUserRole(
                                                    index,
                                                    'branchId',
                                                    e.target.value ? Number(e.target.value) : null
                                                )}
                                                required
                                            >
                                                <option value="">지점을 선택하세요</option>
                                                {branchList.map(branch => (
                                                    <option key={branch.id} value={branch.id}>
                                                        {branch.name}
                                                    </option>
                                                ))}
                                            </select>
                                        </label>
                                    </div>
                                )}

                                {userRole.roleId && !checkRoleRequiresBranch(userRole.roleId) && (
                                    <div className="form-group">
                                        <p className="info-text">이 역할은 지점 선택이 필요하지 않습니다. 모든 지점에 접근 가능합니다.</p>
                                    </div>
                                )}
                            </div>
                        </div>
                    ))}

                    <button
                        type="button"
                        className="add-role-button"
                        onClick={addUserRole}
                    >
                        + 권한 추가
                    </button>
                </div>


                <div className="submit-button-container">
                    <button
                        type="button"
                        onClick={handleBackToLogin}
                        className="cancel-button"
                    >
                        취소
                    </button>
                    <button
                        type="submit"
                        disabled={isSubmitting || success}
                        className="submit-button"
                    >
                        {isSubmitting ? '처리 중...' : '회원가입'}
                    </button>
                </div>
            </form>
        </div>
    );
}
